import type { SendInviteBody } from '@si/types';
import { createInviteResponseSchema } from '@si/types';
import { ensureSiAdmin, post, useTranslate } from '@si/utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

import { queryKeys } from './queryKeys';
import { useUser } from './useUser';

export function useSendInvite() {
  const queryClient = useQueryClient();
  const { user } = useUser();
  const { t, translate } = useTranslate();

  const { mutate, error, isPending, isSuccess, reset } = useMutation({
    mutationFn: (params: SendInviteBody) => {
      ensureSiAdmin(user, translate(t.adminAccessRequired));
      return post({
        url: `/api/admin/invites`,
        body: params,
        schema: createInviteResponseSchema,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.getInvites,
      });
    },
  });

  const sendInvite = useCallback((params: SendInviteBody) => mutate(params), [mutate]);

  return {
    sendInvite,
    error,
    isPending,
    isSuccess,
    reset,
  };
}
